// Offline Sales Queue using IndexedDB
const DB_NAME = 'kasseport_offline';
const DB_VERSION = 1;
const STORE_NAME = 'pending_sales';

class OfflineSalesQueue {
  constructor() {
    this.db = null;
    this.isOnline = navigator.onLine;
    this.listeners = [];
    
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'localId' });
          store.createIndex('status', 'status', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
        }
      };
    });
  }

  handleOnline() {
    this.isOnline = true;
    this.notifyListeners();
    this.syncPendingSales();
  }

  handleOffline() {
    this.isOnline = false;
    this.notifyListeners();
  }

  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  notifyListeners() {
    this.listeners.forEach(callback => callback({
      isOnline: this.isOnline,
      pendingCount: this.getPendingCountSync()
    }));
  }

  getPendingCountSync() {
    // This is called synchronously, actual count fetched async
    return this._pendingCount || 0;
  }

  async queueSale(sale) {
    if (!this.db) await this.init();
    
    const localId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const queuedSale = {
      localId,
      ...sale,
      status: 'pending',
      createdAt: new Date().toISOString(),
      attempts: 0
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.add(queuedSale);
      
      request.onsuccess = () => {
        this.updatePendingCount();
        // Request background sync if available
        if ('serviceWorker' in navigator && 'sync' in window.registration) {
          navigator.serviceWorker.ready.then(registration => {
            registration.sync.register('sync-sales');
          });
        }
        resolve(queuedSale);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingSales() {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('status');
      const request = index.getAll('pending');
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllSales() {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async updateSaleStatus(localId, status, serverId = null) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(localId);
      
      getRequest.onsuccess = () => {
        const sale = getRequest.result;
        if (sale) {
          sale.status = status;
          sale.serverId = serverId;
          sale.syncedAt = status === 'synced' ? new Date().toISOString() : null;
          const putRequest = store.put(sale);
          putRequest.onsuccess = () => {
            this.updatePendingCount();
            resolve(sale);
          };
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve(null);
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async incrementAttempts(localId) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(localId);
      
      getRequest.onsuccess = () => {
        const sale = getRequest.result;
        if (sale) {
          sale.attempts = (sale.attempts || 0) + 1;
          sale.lastAttempt = new Date().toISOString();
          const putRequest = store.put(sale);
          putRequest.onsuccess = () => resolve(sale);
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve(null);
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async deleteSale(localId) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(localId);
      
      request.onsuccess = () => {
        this.updatePendingCount();
        resolve(true);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async clearSyncedSales() {
    if (!this.db) await this.init();
    
    const allSales = await this.getAllSales();
    const syncedSales = allSales.filter(s => s.status === 'synced');
    
    for (const sale of syncedSales) {
      await this.deleteSale(sale.localId);
    }
    
    return syncedSales.length;
  }

  async updatePendingCount() {
    const pending = await this.getPendingSales();
    this._pendingCount = pending.length;
    this.notifyListeners();
  }

  async syncPendingSales() {
    if (!this.isOnline) return { synced: 0, failed: 0 };
    
    const pendingSales = await this.getPendingSales();
    let synced = 0;
    let failed = 0;
    
    for (const sale of pendingSales) {
      try {
        await this.incrementAttempts(sale.localId);
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sales`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('kasseport_token')}`
          },
          body: JSON.stringify({
            items: sale.items,
            total: sale.total
          })
        });
        
        if (response.ok) {
          const serverSale = await response.json();
          await this.updateSaleStatus(sale.localId, 'synced', serverSale.id);
          synced++;
        } else {
          const error = await response.json();
          if (response.status === 400 && error.detail?.includes('Insufficient stock')) {
            // Mark as failed if stock issue
            await this.updateSaleStatus(sale.localId, 'failed');
          }
          failed++;
        }
      } catch (error) {
        console.error('Sync error for sale:', sale.localId, error);
        failed++;
      }
    }
    
    this.updatePendingCount();
    return { synced, failed };
  }
}

// Singleton instance
const offlineQueue = new OfflineSalesQueue();

export default offlineQueue;
